import { useState, type ChangeEvent } from 'react';

/**
 * SIP Tools live panel — calls the sip-tools-svc worker for the stateless tools
 * (Upload SIP Logs, Hop Trace, SIP code lookup). The worker base URL is set in
 * the UI (persisted in localStorage) so no SPA redeploy is needed to point it.
 *
 * area.key: 'upload' → Upload SIP Logs ; 'single' → Hop Trace ;
 * 'signaling' → SIP code lookup. Other sip areas fall back to AreaPanel.
 */

type Hop = { seq: number; host: string; display: string; role: string; is_dev: boolean };
type Decoded = { code: number; name: string; family: string; line?: string };
type LadderRow = { ts: string; dir: string; line: string; is_response: boolean; code: number | null; src?: string; dst?: string };
type Session = {
  verdict: 'PASS' | 'WARN' | 'FAIL'; reasons: string[]; flags: string[];
  media_status: string; mos: number | null; numbers: string[];
  codes_seen: Record<string, number>; decoded: Decoded[];
  hop_count: number; hops: Hop[]; dev_gateway?: boolean; leg_count: number;
  ladder: LadderRow[]; start: string;
};
type AnalyzeResult = { n_events: number; n_sessions: number; summary: Record<string, number>; sessions: Session[] };

const VBADGE: Record<string, string> = {
  PASS: 'text-emerald-600 border-emerald-600 bg-emerald-50',
  WARN: 'text-amber-600 border-amber-600 bg-amber-50',
  FAIL: 'text-rose-600 border-rose-600 bg-rose-50',
};
const codeColor = (c: number | null, isResp: boolean) => {
  if (!isResp || !c) return 'text-sky-700';
  if (c < 200) return 'text-gray-500';
  if (c < 300) return 'text-emerald-600';
  if (c < 400) return 'text-amber-600';
  return 'text-rose-600';
};

function useApiBase(): [string, (v: string) => void] {
  const [base, setBase] = useState<string>(() => localStorage.getItem('sipToolsApi') || '');
  const set = (v: string) => { localStorage.setItem('sipToolsApi', v.replace(/\/+$/, '')); setBase(v.replace(/\/+$/, '')); };
  return [base, set];
}

export default function SipToolsPanel({ areaKey }: { areaKey: string }) {
  const [base, setBase] = useApiBase();
  return (
    <div className="space-y-3">
      <div className="card p-3 flex items-center gap-2 text-sm">
        <label className="text-gray-500 whitespace-nowrap">Worker URL</label>
        <input
          className="flex-1 border rounded px-2 py-1 font-mono text-xs"
          placeholder="https://sip-tools-svc.<your-subdomain>.workers.dev"
          value={base}
          onChange={(e) => setBase(e.target.value)}
        />
        {base && <a className="text-indigo-600 text-xs" href={`${base}/health`} target="_blank" rel="noreferrer">health ↗</a>}
      </div>
      {!base && <div className="card p-3 text-sm text-amber-700">Deploy <code>sip-tools-svc</code> (<code>wrangler deploy</code>), then paste its <code>workers.dev</code> URL above.</div>}
      {areaKey === 'upload' && <UploadPanel base={base} />}
      {areaKey === 'single' && <><ActiveTestPanel base={base} /><HopTracePanel base={base} /></>}
      {areaKey === 'bulk' && <BulkPanel base={base} />}
      {areaKey === 'signaling' && <CodeLookupPanel base={base} />}
    </div>
  );
}

async function readFiles(list: FileList): Promise<{ name: string; content: string }[]> {
  return Promise.all(Array.from(list).map(async (f) => ({ name: f.name, content: await f.text() })));
}

function VerdictChips({ summary }: { summary: Record<string, number> }) {
  return (
    <div className="flex gap-2 text-xs font-semibold">
      <span className="px-2 py-0.5 rounded bg-emerald-50 text-emerald-600">PASS {summary.PASS || 0}</span>
      <span className="px-2 py-0.5 rounded bg-amber-50 text-amber-600">WARN {summary.WARN || 0}</span>
      <span className="px-2 py-0.5 rounded bg-rose-50 text-rose-600">FAIL {summary.FAIL || 0}</span>
    </div>
  );
}

function SessionCard({ s, i }: { s: Session; i: number }) {
  return (
    <div className="card p-0 overflow-hidden">
      <div className="p-3 border-b flex flex-wrap items-center gap-2 text-sm">
        <span className={`border-2 rounded px-2 font-bold ${VBADGE[s.verdict]}`}>{s.verdict}</span>
        <b>Session {i + 1}</b><span className="text-gray-500">start {s.start}</span>
        <span className="text-gray-500">· legs {s.leg_count} · {s.hop_count} hops · media <b>{s.media_status}</b>
          {s.mos != null && <> · MOS {s.mos}</>}</span>
        <span className="text-gray-500">· {s.numbers.join(', ') || '—'}</span>
      </div>
      <ul className="px-5 py-2 list-disc text-sm">{s.reasons.map((r, k) => <li key={k}>{r}</li>)}</ul>
      {s.flags.length > 0 && <div className="px-3 pb-2 flex flex-wrap gap-1">
        {s.flags.map((f) => <span key={f} className="text-xs bg-violet-50 text-violet-700 rounded px-2 py-0.5 font-semibold">{f}</span>)}
      </div>}
      {s.hops.length > 0 && <div className="px-3 pb-2 text-xs">
        <div className="text-gray-500 mb-1 font-semibold">HOPS ({s.hop_count})</div>
        <div className="flex flex-wrap gap-1 items-center">
          {s.hops.map((h) => (
            <span key={h.seq} className={`rounded px-2 py-0.5 ${h.is_dev ? 'bg-rose-50 text-rose-700 font-bold' : 'bg-gray-100 text-gray-700'}`}>
              {h.seq}. {h.display}{h.is_dev ? ' ⚠' : ''}
            </span>
          ))}
        </div>
      </div>}
      <table className="w-full text-xs font-mono">
        <thead><tr className="text-gray-400 text-left"><th className="px-3 py-1">Time</th><th>Dir</th><th>Message</th><th>Route</th></tr></thead>
        <tbody>
          {s.ladder.map((e, k) => (
            <tr key={k} className="border-t border-gray-50">
              <td className="px-3 py-0.5 text-gray-400 whitespace-nowrap">{e.ts}</td>
              <td className="text-gray-400">{e.dir}</td>
              <td className={`font-semibold ${codeColor(e.code, e.is_response)}`}>{e.line.slice(0, 78)}</td>
              <td className="text-gray-400 whitespace-nowrap">{e.src && e.dst ? `${e.src} → ${e.dst}` : ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function UploadPanel({ base }: { base: string }) {
  const [res, setRes] = useState<AnalyzeResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const onPick = async (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length || !base) return;
    setBusy(true); setErr(null);
    try {
      const files = await readFiles(e.target.files);
      const r = await fetch(`${base}/tool/sip_logs_analyze`, {
        method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ files }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
      setRes(data);
    } catch (e2) { setErr(String(e2)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <div className="card p-3 text-sm">
        <label className="font-semibold">Upload SIP Logs</label>
        <p className="text-gray-500 text-xs mb-2">Webex Control Hub SIP JSON, CUBE/LGW <code>debug ccsip</code> text, or any raw SIP dump. Multiple files are consolidated into one decoded ladder.</p>
        <input type="file" multiple disabled={!base || busy} onChange={onPick} className="text-sm" />
        {busy && <span className="ml-2 text-gray-500">analyzing…</span>}
      </div>
      {err && <div className="card p-3 text-sm text-rose-600">{err}</div>}
      {res && <>
        <div className="card p-3 flex items-center gap-3 text-sm">
          <span>{res.n_events} messages · {res.n_sessions} session(s)</span><VerdictChips summary={res.summary} />
        </div>
        {res.sessions.map((s, i) => <SessionCard key={i} s={s} i={i} />)}
      </>}
    </div>
  );
}

function HopTracePanel({ base }: { base: string }) {
  const [src, setSrc] = useState(''); const [dst, setDst] = useState('');
  const [files, setFiles] = useState<{ name: string; content: string }[]>([]);
  const [res, setRes] = useState<{ matched?: boolean; verdict: string; hop_count: number; hops: Hop[]; dev_gateway?: boolean } | null>(null);
  const [busy, setBusy] = useState(false); const [err, setErr] = useState<string | null>(null);
  const run = async () => {
    if (!base || !files.length) return;
    setBusy(true); setErr(null);
    try {
      const r = await fetch(`${base}/tool/hop_trace`, {
        method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ files, src, dst }),
      });
      const data = await r.json(); if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`); setRes(data);
    } catch (e) { setErr(String(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <div className="card p-3 text-sm space-y-2">
        <label className="font-semibold">Hop Trace — source → destination</label>
        <p className="text-gray-500 text-xs">How many hops a call hits between two numbers. Provide the captured logs and the two numbers.</p>
        <div className="flex flex-wrap gap-2">
          <input className="border rounded px-2 py-1 text-sm" placeholder="Source number" value={src} onChange={(e) => setSrc(e.target.value)} />
          <input className="border rounded px-2 py-1 text-sm" placeholder="Destination number" value={dst} onChange={(e) => setDst(e.target.value)} />
          <input type="file" multiple className="text-sm" onChange={async (e) => e.target.files && setFiles(await readFiles(e.target.files))} />
          <button disabled={!base || !files.length || busy} onClick={run} className="bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 text-white rounded px-3 py-1 text-sm">Trace hops</button>
        </div>
      </div>
      {err && <div className="card p-3 text-sm text-rose-600">{err}</div>}
      {res && (
        <div className="card p-3 text-sm">
          <div className="flex items-center gap-2 mb-2">
            <span className={`border-2 rounded px-2 font-bold ${VBADGE[res.verdict] || ''}`}>{res.verdict}</span>
            <b>{res.hop_count} hops</b>
            <span className="text-gray-500">{src || '?'} → {dst || '?'} {res.matched ? '(matched)' : '(closest session)'}</span>
            {res.dev_gateway && <span className="text-xs bg-rose-50 text-rose-700 rounded px-2 py-0.5 font-semibold">dev gateway in path ⚠</span>}
          </div>
          <ol className="text-xs space-y-0.5">
            {res.hops.map((h) => (
              <li key={h.seq} className={h.is_dev ? 'text-rose-700 font-bold' : ''}>
                {h.seq}. <span className="font-mono">{h.display}</span> — {h.role}{h.is_dev ? '  ⚠ DEV' : ''}
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

function CodeLookupPanel({ base }: { base: string }) {
  const [codes, setCodes] = useState('200, 302, 486, 503, 603');
  const [res, setRes] = useState<Decoded[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const run = async () => {
    if (!base) return; setErr(null);
    try {
      const list = codes.split(/[,\s]+/).map((x) => parseInt(x, 10)).filter((n) => !isNaN(n));
      const r = await fetch(`${base}/tool/sip_codes_lookup`, {
        method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ codes: list }),
      });
      const data = await r.json(); if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`); setRes(data.results);
    } catch (e) { setErr(String(e)); }
  };
  return (
    <div className="card p-3 text-sm space-y-2">
      <label className="font-semibold">SIP Signaling — code lookup (1xx → 6xx)</label>
      <div className="flex gap-2">
        <input className="flex-1 border rounded px-2 py-1 text-sm font-mono" value={codes} onChange={(e) => setCodes(e.target.value)} />
        <button disabled={!base} onClick={run} className="bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 text-white rounded px-3 py-1 text-sm">Decode</button>
      </div>
      {err && <div className="text-rose-600">{err}</div>}
      {res && (
        <table className="w-full text-xs mt-1">
          <tbody>{res.map((d) => (
            <tr key={d.code} className="border-t border-gray-50"><td className="py-1 font-mono font-bold w-12">{d.code}</td><td className="font-semibold">{d.name}</td><td className="text-gray-500">{d.family}</td></tr>
          ))}</tbody>
        </table>
      )}
    </div>
  );
}

function ActiveTestPanel({ base }: { base: string }) {
  const [number, setNumber] = useState('');
  const [direction, setDirection] = useState('outbound');
  const [res, setRes] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const run = async (exec: boolean) => {
    if (!base || !number) return;
    setBusy(true); setErr(null); setRes(null);
    try {
      const r = await fetch(`${base}/tool/pstn_test_run`, {
        method: 'POST', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ number, direction, exec }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
      setRes(data);
    } catch (e) { setErr(String(e)); } finally { setBusy(false); }
  };
  return (
    <div className="card p-3 text-sm space-y-2">
      <label className="font-semibold">Active test — originate a real test call</label>
      <p className="text-gray-500 text-xs">Runs on the spoke. Dry‑run shows the SIPp command; live run is allow‑list gated by Shield.</p>
      <div className="flex flex-wrap gap-2 items-center">
        <input className="border rounded px-2 py-1 text-sm" placeholder="Number (DID)" value={number} onChange={(e) => setNumber(e.target.value)} />
        <select className="border rounded px-2 py-1 text-sm" value={direction} onChange={(e) => setDirection(e.target.value)}>
          <option value="outbound">Outbound</option><option value="inbound">Inbound</option>
        </select>
        <button disabled={!base || !number || busy} onClick={() => run(false)} className="border rounded px-3 py-1 text-sm disabled:opacity-40">Dry run</button>
        <button disabled={!base || !number || busy} onClick={() => run(true)} className="bg-indigo-600 hover:bg-indigo-500 text-white rounded px-3 py-1 text-sm disabled:opacity-40">Run live test</button>
        {busy && <span className="text-gray-500">testing…</span>}
      </div>
      {err && <div className="text-rose-600">{err}</div>}
      {res && res.verdict === 'DRYRUN' && <div className="font-mono text-xs bg-gray-50 rounded p-2 break-all">{res.command}</div>}
      {res && res.verdict && res.verdict !== 'DRYRUN' && (
        <div className="flex flex-wrap items-center gap-2">
          <span className={`border-2 rounded px-2 font-bold ${VBADGE[res.verdict] || ''}`}>{res.verdict}</span>
          <span className="text-gray-500">media <b>{res.media_status}</b>{res.mos != null && <> · MOS {res.mos}</>} · codes {Object.keys(res.codes_seen || {}).join(', ')}</span>
          {(res.reasons || []).slice(0, 1).map((r: string, k: number) => <span key={k} className="text-gray-600">— {r}</span>)}
        </div>
      )}
    </div>
  );
}

function BulkPanel({ base }: { base: string }) {
  const [text, setText] = useState('');
  const [direction, setDirection] = useState('outbound');
  const [approved, setApproved] = useState(false);
  const [exec, setExec] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [status, setStatus] = useState<string>('');
  const [err, setErr] = useState<string | null>(null);
  const numbers = () => text.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);
  const poll = async (jobId: string) => {
    for (let i = 0; i < 180; i++) {
      await new Promise((r) => setTimeout(r, 2000));
      const r = await fetch(`${base}/tool/pstn_test_result`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ job_id: jobId }) });
      const d = await r.json();
      if (d.status === 'done') { setResult(d.result); setStatus('done'); return; }
      if (d.status === 'error') { setErr(d.error || 'job error'); setStatus(''); return; }
      setStatus(`running… (${i + 1})`);
    }
    setStatus('');
  };
  const run = async () => {
    if (!base) return;
    setErr(null); setResult(null); setStatus('starting…');
    try {
      const r = await fetch(`${base}/tool/pstn_test_bulk`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ numbers: numbers(), direction, exec, approved }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      if (d.job_id) poll(d.job_id); else { setResult(d); setStatus('done'); }
    } catch (e) { setErr(String(e)); setStatus(''); }
  };
  const exportCsv = () => {
    const rows = (result?.roster || []) as any[];
    const csv = ['number,verdict,hop_count,media_status,codes,flags']
      .concat(rows.map((x) => `${x.number},${x.verdict},${x.hop_count ?? ''},${x.media_status ?? ''},${(x.codes || []).join('|')},${(x.flags || []).join('|')}`))
      .join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = 'sip_bulk_roster.csv'; a.click();
  };
  return (
    <div className="space-y-3">
      <div className="card p-3 text-sm space-y-2">
        <label className="font-semibold">Bulk test — block of DIDs</label>
        <textarea className="w-full border rounded p-2 font-mono text-xs h-24" placeholder="One number per line (or comma-separated)" value={text} onChange={(e) => setText(e.target.value)} />
        <div className="flex flex-wrap gap-3 items-center">
          <span className="text-gray-500">{numbers().length} numbers</span>
          <select className="border rounded px-2 py-1 text-sm" value={direction} onChange={(e) => setDirection(e.target.value)}>
            <option value="outbound">Outbound</option><option value="inbound">Inbound</option>
          </select>
          <label className="flex items-center gap-1"><input type="checkbox" checked={exec} onChange={(e) => setExec(e.target.checked)} /> Live (exec)</label>
          <label className="flex items-center gap-1"><input type="checkbox" checked={approved} onChange={(e) => setApproved(e.target.checked)} /> Approved</label>
          <button disabled={!base || !numbers().length} onClick={run} className="bg-indigo-600 hover:bg-indigo-500 text-white rounded px-3 py-1 text-sm disabled:opacity-40">Run bulk</button>
          {status && <span className="text-gray-500">{status}</span>}
        </div>
        <p className="text-gray-400 text-xs">Live bulk requires both checkboxes and an allow‑listed set (Shield gate).</p>
      </div>
      {err && <div className="card p-3 text-sm text-rose-600">{err}</div>}
      {result && (
        <div className="card p-0 overflow-hidden">
          <div className="p-3 border-b flex items-center gap-2 text-sm">
            <VerdictChips summary={result.summary || {}} />
            <span className="text-gray-500">· {result.count ?? (result.roster || []).length} numbers</span>
            <button onClick={exportCsv} className="ml-auto border rounded px-2 py-0.5 text-xs">Export CSV</button>
          </div>
          <table className="w-full text-xs">
            <thead><tr className="text-gray-400 text-left"><th className="px-3 py-1">Number</th><th>Verdict</th><th>Hops</th><th>Media</th><th>Codes</th></tr></thead>
            <tbody>
              {(result.roster || []).map((x: any, k: number) => (
                <tr key={k} className="border-t border-gray-50">
                  <td className="px-3 py-1 font-mono">{x.number}</td>
                  <td><span className={`rounded px-1.5 font-semibold ${VBADGE[x.verdict] || 'text-gray-500'}`}>{x.verdict}</span></td>
                  <td>{x.hop_count ?? ''}</td><td>{x.media_status ?? ''}</td>
                  <td className="font-mono text-gray-500">{(x.codes || []).join(', ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
